import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual } from "lodash-es";
import {
	type FieldValues,
	type Resolver,
	type UseFormSetValue,
	useFieldArray,
	useForm,
	useWatch,
} from "react-hook-form";
import { getCollapsedEntryId } from "web_myplann/Finances/Helpers/getCollapsedEntryId";
import type { TentativeEntriesModel } from "web_myplann/Finances/Types/TentativeEntriesModel";
import { type ZodType, z } from "zod";

type TentativeEntry = FieldValues & { id: number | null };

type WideModel = TentativeEntriesModel<FieldValues>;

type Params<TEntry extends TentativeEntry> = {
	entrySchema: ZodType<TEntry>;
	entries: TEntry[];
	isDetailed: boolean;
	sum: (entries: TEntry[]) => number;
	createPlaceholder: (id: number | null, total: number) => TEntry;
	save: (entries: TEntry[]) => void;
};

export const useTentativeEntriesForm = <TEntry extends TentativeEntry>({
	entrySchema,
	entries,
	isDetailed,
	sum,
	createPlaceholder,
	save,
}: Params<TEntry>) => {
	const initialValues = {
		entries,
		totalValue: sum(entries),
		exact: isDetailed,
	};

	const { control, setValue, handleSubmit } = useForm<WideModel>({
		resolver: zodResolver(
			z.object({
				entries: z.array(entrySchema),
				totalValue: z.number().optional(),
				exact: z.boolean().optional(),
			}),
		) as unknown as Resolver<WideModel>,
		values: initialValues,
	});

	const { fields, append, replace, remove } = useFieldArray({
		control,
		name: "entries",
	});

	const entryKeys = fields.map((field) => field.id);

	const currentEntries = (useWatch({ control, name: "entries" }) ??
		[]) as TEntry[];
	const exact = useWatch({ control, name: "exact" }) ?? false;
	const manualTotal = useWatch({ control, name: "totalValue" }) ?? 0;
	const detailedTotal = sum(currentEntries);
	const total = exact ? detailedTotal : manualTotal;

	const hasUnconfirmedChanges = !isEqual(
		{ entries: currentEntries, totalValue: manualTotal, exact },
		initialValues,
	);

	const setExact = (checked: boolean) => {
		if (!checked && currentEntries.length > 0) {
			setValue("totalValue", detailedTotal);
		}
		setValue("exact", checked);
	};

	const setManualTotal = (value: number) => setValue("totalValue", value);

	const removeEntry = (index: number) => {
		remove(index);
		if (fields.length === 1) {
			setValue("exact", false);
		}
	};

	const onSubmit = handleSubmit((data) => {
		if (data.exact) {
			save(data.entries as TEntry[]);
			return;
		}

		save([
			createPlaceholder(getCollapsedEntryId(entries), data.totalValue ?? 0),
		]);
	});

	return {
		entries: currentEntries,
		entryKeys,
		append: append as unknown as (entry: TEntry) => void,
		replace: replace as unknown as (entries: TEntry[]) => void,
		removeEntry,
		setValue: setValue as unknown as UseFormSetValue<
			TentativeEntriesModel<TEntry>
		>,
		onSubmit,
		exact,
		total,
		setExact,
		setManualTotal,
		hasUnconfirmedChanges,
	};
};
